import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CustomReportSettings } from "../../types";

export type RelativeDateRangeType = 'ThisMonth' | 'LastMonth' | 'Last7Days' | 'Last30Days' | 'ThisYear' | 'LastYear' | 'RollingDays' | 'RollingMonths' | 'Custom';
export interface RelativeDateRange {
	type: RelativeDateRangeType;
	value?: number; // used for RollingDays / RollingMonths
}

export interface SavedReport {
	id: string;
	name: string;
	settings: CustomReportSettings;
	modified: string; // ISO string
	relativeRange?: RelativeDateRange; // optional relative date range descriptor
}

interface SavedReportsState {
	value: SavedReport[];
}

const STORAGE_KEY = 'savedCustomReports';

const loadFromStorage = (): SavedReport[] => {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (!raw) return [];
		const parsed = JSON.parse(raw);
		return Array.isArray(parsed) ? parsed : [];
	} catch {
		return [];
	}
};

const saveToStorage = (list: SavedReport[]) => {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
	} catch {
		// ignore
	}
};

const initialState: SavedReportsState = {
	value: loadFromStorage()
};

const savedReportsSlice = createSlice({
	name: 'savedReports',
	initialState,
	reducers: {
		addSavedReport: (state, action: PayloadAction<{ name: string; settings: CustomReportSettings; relativeRange?: RelativeDateRange }>) => {
			const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
			const item: SavedReport = { id, name: action.payload.name, settings: action.payload.settings, modified: new Date().toISOString(), relativeRange: action.payload.relativeRange };
			state.value.push(item);
			saveToStorage(state.value);
		},
		updateSavedReport: (state, action: PayloadAction<{ id: string; name?: string; settings?: CustomReportSettings; relativeRange?: RelativeDateRange }>) => {
			const idx = state.value.findIndex(r => r.id === action.payload.id);
			if (idx >= 0) {
				const current = state.value[idx];
				state.value[idx] = {
					...current,
					name: action.payload.name ?? current.name,
					settings: action.payload.settings ?? current.settings,
					modified: new Date().toISOString(),
					relativeRange: action.payload.relativeRange ?? current.relativeRange
				};
				saveToStorage(state.value);
			}
		},
		deleteSavedReport: (state, action: PayloadAction<string>) => {
			state.value = state.value.filter(r => r.id !== action.payload);
			saveToStorage(state.value);
		},
		setSavedReports: (state, action: PayloadAction<SavedReport[]>) => {
			state.value = action.payload;
			saveToStorage(state.value);
		}
	}
});

export const { addSavedReport, updateSavedReport, deleteSavedReport, setSavedReports } = savedReportsSlice.actions;
export default savedReportsSlice.reducer;
